import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axiosConfig';
import Layout from '../components/Layout';
import AttendanceList from '../components/AttendanceList';

const AttendanceListPage = () => {
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const fetchAttendanceRecords = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/attendance/records', {
        params: {
          startDate: filters.startDate,
          endDate: filters.endDate
        }
      });
      setAttendanceRecords(response.data.data || response.data);
    } catch (error) {
      console.error('Error fetching attendance records:', error);
      setAttendanceRecords([]);
    } finally {
      setLoading(false);
    }
  }, [filters.startDate, filters.endDate]);

  useEffect(() => {
    fetchAttendanceRecords();
  }, [fetchAttendanceRecords]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleRefresh = () => {
    fetchAttendanceRecords();
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Attendance Records - ${filters.startDate} to ${filters.endDate}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              color: #000;
              line-height: 1.4;
            }
            .print-header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #000;
              padding-bottom: 15px;
            }
            h1 {
              margin: 0;
              font-size: 24px;
            }
            h2 {
              margin: 5px 0;
              font-size: 16px;
              font-weight: normal;
              color: #555;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px 12px;
              text-align: left;
            }
            th {
              background-color: #f8f8f8;
              font-weight: bold;
            }
            .footer {
              margin-top: 30px;
              text-align: right;
              font-size: 12px;
              color: #666;
            }
            @page {
              margin: 0.5in;
              size: letter;
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>Attendance Records</h1>
            <h2>${filters.startDate} to ${filters.endDate}</h2>
          </div>
          <table>
            <thead>
              <tr>
                <th>Employee ID</th>
                <th>Name</th>
                <th>Time In</th>
                <th>Time Out</th>
              </tr>
            </thead>
            <tbody>
              ${attendanceRecords.map(record => `
                <tr>
                  <td>${record.employee.employeeId}</td>
                  <td>${record.employee.name}</td>
                  <td>${new Date(record.timeIn).toLocaleString('en', { timeZone: 'Asia/Hong_Kong' })}</td>
                  <td>${record.timeOut ? new Date(record.timeOut).toLocaleString('en', { timeZone: 'Asia/Hong_Kong' }) : 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="footer">
            Generated on ${new Date().toLocaleString('en', { timeZone: 'Asia/Hong_Kong' })}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleExportCSV = () => {
    const convertToCSV = (records) => {
      const header = ['Employee ID', 'Name', 'Time In', 'Time Out'];
      const rows = records.map(record => [
        record.employee.employeeId,
        record.employee.name,
        new Date(record.timeIn).toLocaleString('en', { timeZone: 'Asia/Hong_Kong' }),
        record.timeOut ? new Date(record.timeOut).toLocaleString('en', { timeZone: 'Asia/Hong_Kong' }) : 'Not Yet Timed Out'
      ]);

      return [
        header.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');
    };

    const csv = convertToCSV(attendanceRecords);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-records-${filters.startDate}-to-${filters.endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto bg-white rounded shadow p-6 mt-6">
        <h1 className="text-2xl font-bold mb-6">Attendance Records</h1>
        
        {/* Filters and Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="w-full bg-teal-500 text-white py-2 px-4 rounded-md hover:bg-teal-600 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handlePrint}
              disabled={loading || attendanceRecords.length === 0}
              className="flex-1 bg-blue-500 text-white py-2 px-3 rounded-md hover:bg-blue-600 disabled:opacity-50 text-sm"
              title="Print / Save as PDF"
            >
              Print
            </button>
            <button
              onClick={handleExportCSV}
              disabled={loading || attendanceRecords.length === 0}
              className="flex-1 bg-green-500 text-white py-2 px-3 rounded-md hover:bg-green-600 disabled:opacity-50 text-sm"
            >
              Export CSV
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-center text-gray-500 py-8">Loading attendance records...</p>
        ) : attendanceRecords.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No attendance records found for the selected date range.</p>
        ) : (
          <AttendanceList attendanceRecords={attendanceRecords} />
        )}
      </div>
    </Layout>
  );
};

export default AttendanceListPage;
